import React, { useMemo, useState, useEffect } from 'react';
import { Button, TableCell, TableRow, Box } from '@mui/material';
import CustomTypography from '../../ui/atoms/CustomTypography';
import IconImages from '../../assets/icons/IconImages';
import CustomTooltip from '../../ui/atoms/CustomTooltip';
import { useVisualisationRequired } from '../../utils/helpers/formatValue';
import {
  AuditStateQuestion,
  MobileStateItemBase,
  SaveDataAnswerGroup,
} from '@safeguardproperties/corescript-react-npm/dist/types';
import QuestionItemView from './QuestionItemView';
import { RootState, useAppSelector } from '../../redux/Store';
import {
  getScriptState,
  getScriptSave,
} from '../../redux/reducer/coreScriptReducer';
import QuestionGallery from './QuestionGallery';
import { useSelector } from 'react-redux';
import ErrorMessage from '../../ui/atoms/ErrorMessage';
import WarnMessage from '../../ui/atoms/WarnMessage';

type QuestionItemProps = {
  index: number;
  question: MobileStateItemBase | AuditStateQuestion | any;
  answers: SaveDataAnswerGroup | undefined;

  keyToString: (key: string) => string;
  onSetAnswer?: (questionKey: string, answers: string[]) => void;
};

const unsetOnSetAnswer = (questionKey: string, answers: string[]) => {
  console.error('On Set Answer was not passed to question', {
    QuestionPath: questionKey,
    Answers: answers,
  });
};

type ImageResult = {
  have: number;
  total: number;
};

const Question = ({
  question,
  index,
  answers,
  keyToString,
  onSetAnswer,
}: QuestionItemProps) => {
  // Determine background color based on index
  const backgroundColor = index % 2 === 1 ? '#FBFBFB' : '#ffffff';
  const question_Title = keyToString(question?.Title);
  const saveData = useSelector(getScriptSave);
  const isReadOnly = useSelector(
    (state: RootState) => state.contextId.isReadOnly || question.ReadOnly
  );

  const script = useAppSelector(getScriptState);

  const imageResult: ImageResult = useMemo(() => {
    if (
      question?.Title &&
      script.Media &&
      script.Media.ByQuestion &&
      script.Media.List
    ) {
      const mediaKeys = script.Media.ByQuestion[question.Title] || [];
      const mediaItems = mediaKeys
        .map((key) => script.Media.List[key]) // Map the keys to media objects
        .filter(Boolean); // Filter out undefined items

      const total = mediaItems.length; // Total media items
      const have = mediaItems.filter((item) => item.Have > 0).length; // Media items with at least one

      return { have, total };
    }
    return { have: 0, total: 0 }; // Default values
  }, [question, script]);

  const [inlineGalleryOpen, setInlineGalleryOpen] = useState(
    imageResult.total > 0
  );
  useEffect(() => {
    if (imageResult.total > 0) {
      setInlineGalleryOpen(true);
    }
  }, [imageResult.total]);

  const getActiveAnswers = useMemo(() => {
    const allAnswers = saveData?.Answers[question?.Title]?.Answers || [];
    return allAnswers.filter((ans: any) => ans?.Active !== false);
  }, [question, saveData]);

  const notAnswered = (() => {
    return (
      getActiveAnswers.length === 0 ||
      (getActiveAnswers.length === 1 && !getActiveAnswers[0]?.Answer)
    );
  })();

  const isInvalidAnswer = (() => {
    if (!question?.Required) return false;
    return notAnswered;
  })();

  const showRedBox =
    (question?.ErrorMessage && question?.ErrorType == 'error') ||
    isInvalidAnswer;
  const warning = question?.ErrorType == 'warning' && question?.ErrorMessage;

  return (
    <React.Fragment>
      {question?.ErrorMessage && !warning && (
        <Box sx={{ display: 'flex', margin: '1rem' }}>
          <ErrorMessage>{keyToString(question?.ErrorMessage)}</ErrorMessage>
        </Box>
      )}
      {warning && (
        <Box sx={{ display: 'flex', margin: '1rem' }}>
          <WarnMessage>{keyToString(question?.ErrorMessage)}</WarnMessage>
        </Box>
      )}
      <TableRow
        sx={{
          display: 'flex',
          flex: 7,
          alignItems: 'center',
          backgroundColor: backgroundColor,
          border: showRedBox ? '1px solid rgba(243, 86, 37, 0.50)' : 'none', // Default: No border
        }}
      >
        <TableCell
          sx={{
            border: 'none',
            fontFamily: 'Montserrat',
            fontSize: '12px',
            fontWeight: 500,
            flex: 2.5,
            lineHeight: '14.63px',
            textAlign: 'left', // Ensures text aligns left
            textUnderlinePosition: 'from-font',
            textDecorationSkipInk: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          {question?.Required && (
            <p style={{ color: '#CB690E', margin: 0 }}>*</p>
          )}
          <CustomTypography
            variant='body1'
            sx={{
              fontFamily: 'Montserrat',
              fontSize: '12px',
              fontWeight: 500,
              lineHeight: '14.63px',
              color: '#343330',
              cursor: 'pointer',
              textAlign: 'left',
              textTransform: 'none',
              display: '-webkit-box',
              WebkitLineClamp: 5,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              wordBreak: 'break-word',
              hyphens: 'auto',
            }}
          >
            {/* {question_Title && question_Title.length > 20 ? ( */}
            <CustomTooltip
              label={keyToString(question_Title)}
              arrow
              placement='top'
            >
              <span>{keyToString(question_Title)}</span>
            </CustomTooltip>
            {/* // ) : (
            //   keyToString(question_Title)
            // )} */}
          </CustomTypography>
        </TableCell>

        <TableCell sx={{ border: 'none', padding: '12px', flex: 2.5 }}>
          <QuestionItemView
            disabled={isReadOnly}
            question={question}
            answers={answers}
            keyToString={keyToString}
            onSetAnswer={onSetAnswer}
          />
        </TableCell>
        {/* Image Result Column */}
        <TableCell
          sx={{
            fontFamily: 'Montserrat',
            fontSize: '12px',
            border: 'none',
            flex: 0.7,
            textAlign: 'left', // Align all content to the left
            display: 'flex', // Flexbox for alignment
            alignItems: 'center', // Vertically center
            gap: '4px', // Space between elements
          }}
        >
          {imageResult.total > 0 && (
            <Button
              sx={{
                padding: 0, // Completely remove all padding
                paddingTop: 0, // Set top padding to 0
                paddingBottom: 0, // Set bottom padding to 0
                paddingLeft: 0, // Set left padding to 0
                paddingRight: 0, // Set right padding to 0
                minWidth: 0, // Remove default min-width for buttons
                display: 'flex', // Ensure horizontal alignment
                alignItems: 'center', // Vertically align content within the button
                textAlign: 'left', // Align button text to the left
              }}
              onClick={() => setInlineGalleryOpen(!inlineGalleryOpen)}
            >
              <IconImages />
              <CustomTypography
                variant='body1'
                sx={{
                  fontFamily: 'Montserrat',
                  fontSize: '12px',
                  fontWeight: 500,
                  lineHeight: '14.63px',
                  color: '#343330',
                  textAlign: 'left', // Align text to the left
                  textUnderlinePosition: 'from-font',
                  textDecorationSkipInk: 'none',
                  marginLeft: '4px', // Add spacing between the icon and text
                }}
              >
                {imageResult?.have}/{imageResult.total}
              </CustomTypography>
            </Button>
          )}
        </TableCell>
      </TableRow>
      {inlineGalleryOpen && <QuestionGallery question={question?.Title} />}
    </React.Fragment>
  );
};

export default Question;
